/* eslint-disable max-lines */
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import {
  SHIFT_DETAILS_FULL_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { type ExternalTimekeepingIntegrationsConfig } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useCallback, useEffect, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { InternalTimeClockTutorialBottomSheet } from "../TimekeepingIntegrations/InternalTimeClockTutorialBottomSheet";
import { UseInternalTimeClockContent } from "../TimekeepingIntegrations/UseInternalTimeClockContent";
import { ActionNotFoundContent } from "./ActionNotFoundContent";
import { ActionSkippedContent } from "./ActionSkippedContent";
import {
  DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
} from "./constants";
import { retryTimekeepingAction } from "./retryTimekeepingAction";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";

export interface ClockOutTimekeepingIntegrationBottomSheetProps {
  modalState: UseModalState;
  config: ExternalTimekeepingIntegrationsConfig;
  shiftId: string;
  workplaceId: string;
  workerId: string;
  workplaceName: string;
}

type ClockOutTimekeepingIntegrationBottomSheetStep =
  | "charting-reminder"
  | "use-time-clock"
  | "action-not-found"
  | "action-skipped"
  | "requesting-clock-out"
  | "requesting-non-instant-pay"
  | "clock-out-success";

export function ClockOutTimekeepingIntegrationBottomSheet(
  props: ClockOutTimekeepingIntegrationBottomSheetProps
) {
  const { modalState, config, shiftId, workplaceId, workplaceName, workerId } = props;

  const history = useHistory();
  const chartingReminderModalState = useModalState();
  const internalTimeClockTutorialModalState = useModalState();

  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  function getFirstStep(): ClockOutTimekeepingIntegrationBottomSheetStep {
    if (!isDefined(config.badgeNumber)) {
      return "action-not-found";
    }

    return shouldShowChartingReminder ? "charting-reminder" : "use-time-clock";
  }

  const firstStep = getFirstStep();

  const [step, setStep] = useState<ClockOutTimekeepingIntegrationBottomSheetStep>(firstStep);

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();
  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();

  const { showErrorToast } = useToast();

  const { wait, skip: skipWait } = useSkippableWait();

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const closeBottomSheet = useCallback(() => {
    skipWait();
    modalState.closeModal();
    chartingReminderModalState.closeModal();
    setStep(firstStep);
  }, [modalState, chartingReminderModalState, skipWait, firstStep]);

  const onClockOutSuccess = useCallback(async () => {
    setStep("clock-out-success");
    await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
    closeBottomSheet();
  }, [closeBottomSheet, wait]);

  const recordClockOut = useCallback(async () => {
    try {
      await retryTimekeepingAction(
        async () =>
          await recordTimekeepingAction({
            shiftId,
            stage: ShiftStage.CLOCK_OUT,
            shiftActionCheck: ActionCheckType.INTEGRATION,
          }),
        {
          shiftId,
          workerId,
          workplaceId,
          action: "clock-out",
        }
      );

      await onClockOutSuccess();

      history.replace(submitTimesheetPath, { clockedOut: true });
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.message === TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE
      ) {
        setStep("action-not-found");
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_FAILED, {
        shiftId,
        workplaceId,
        workerId,
        clockOutType: "timekeeping-integration",
        error: errorMessage,
      });

      showErrorToast(errorMessage);
      closeBottomSheet();
    }
  }, [
    closeBottomSheet,
    history,
    onClockOutSuccess,
    recordTimekeepingAction,
    shiftId,
    showErrorToast,
    submitTimesheetPath,
    workerId,
    workplaceId,
  ]);

  const convertToNonInstantPay = useCallback(async () => {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.EXTERNAL_INTEGRATION_FAILURE,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }, [markNonInstant, shiftId, modalState, showErrorToast, closeBottomSheet]);

  return (
    <>
      {step === "charting-reminder" && shouldShowChartingReminder && (
        <ClockOutChartingReminder
          modalState={chartingReminderModalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onConfirmClockOut={() => {
            setStep("use-time-clock");
            chartingReminderModalState.closeModal();
          }}
          onCancel={() => {
            closeBottomSheet();
          }}
        />
      )}

      <BottomSheet modalState={modalState} canDismiss={false}>
        {step === "use-time-clock" && isDefined(config.badgeNumber) && (
          <UseInternalTimeClockContent
            action="clock-out"
            badgeNumber={config.badgeNumber}
            pinsToEnter={config.pinsToEnterString}
            timeclockLocation={config.timeclockLocationString}
            onActionConfirm={async () => {
              setStep("requesting-clock-out");
              await recordClockOut();
            }}
            onHelp={() => {
              internalTimeClockTutorialModalState.openModal();
            }}
            onCancel={closeBottomSheet}
          />
        )}
        {step === "action-not-found" && (
          <ActionNotFoundContent
            action="clock-out"
            onTryAgain={
              isDefined(config.badgeNumber)
                ? async () => {
                    setStep("requesting-clock-out");
                    await recordClockOut();
                  }
                : undefined
            }
            onSkipAction={() => {
              setStep("action-skipped");
            }}
            onClose={isDefined(config.badgeNumber) ? undefined : closeBottomSheet}
          />
        )}
        {step === "action-skipped" && (
          <ActionSkippedContent
            action="clock-out"
            onAccept={async () => {
              setStep("requesting-non-instant-pay");
              await convertToNonInstantPay();
            }}
            onCancel={() => {
              if (isDefined(config.badgeNumber)) {
                setStep("use-time-clock");
              } else {
                closeBottomSheet();
              }
            }}
          />
        )}
        {step === "requesting-clock-out" && (
          <BottomSheetLoadingContent
            title="Confirming your clock out"
            description="Should only take a few seconds"
          />
        )}
        {step === "clock-out-success" && (
          <TimekeepingActionSuccessContent
            title="Clocked out"
            description="We hope you had a great shift!"
            onClose={closeBottomSheet}
          />
        )}
        {step === "requesting-non-instant-pay" && (
          <BottomSheetLoadingContent
            title="Converting to non-instant pay"
            description="Should only take a few seconds"
          />
        )}
      </BottomSheet>
      {isDefined(config.badgeNumber) ? (
        <InternalTimeClockTutorialBottomSheet
          modalState={internalTimeClockTutorialModalState}
          action="clock-out"
          steps={config.tutorial.stepsClockOut}
          placeholders={{ workplaceName, badgeNumber: config.badgeNumber }}
          onTimeclockActionPerformed={async () => {
            internalTimeClockTutorialModalState.closeModal();

            setStep("requesting-clock-out");
            await recordClockOut();
          }}
          onTimeclockNotWorking={() => {
            internalTimeClockTutorialModalState.closeModal();
            setStep("action-skipped");
          }}
        />
      ) : null}
    </>
  );
}
/* eslint-enable max-lines */
